import { useState, useRef, useEffect } from "react";
import { IoSend } from "react-icons/io5";
import { MdOutlineInfo } from "react-icons/md";

import styles from "./QuestionInput.module.css";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const wasDisabledRef = useRef<boolean>(false);
    const wasFocusedRef = useRef<boolean>(false);

    // Track when disabled state changes from true to false to restore focus
    useEffect(() => {
        if (wasDisabledRef.current && !disabled && wasFocusedRef.current) {
            // Input was disabled and was focused before - restore focus
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 100); // Small delay to ensure DOM is updated
        }
        wasDisabledRef.current = disabled;
    }, [disabled]);

    // Track focus state
    const handleFocus = () => {
        wasFocusedRef.current = true;
    };

    const handleBlur = () => {
        // Only clear focus tracking if not disabled (to preserve focus intent during loading)
        if (!disabled) {
            wasFocusedRef.current = false;
        }
    };

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = ev.target.value;
        if (newValue.length <= 1000) {
            setQuestion(newValue);
        }
    };

    const sendQuestionDisabled = disabled || !question.trim();

    return (
        <div className={styles.questionInputWrapper}>
            <div className={styles.inputContainer}>
                <textarea
                    ref={textareaRef}
                    className={styles.messageInput}
                    placeholder={placeholder}
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={disabled}
                    rows={2}
                />
                <button
                    className={styles.sendButton}
                    onClick={sendQuestion}
                    disabled={sendQuestionDisabled}
                    aria-label="Send message"
                >
                    <IoSend />
                </button>
            </div>

            {/* Disclaimer Message */}
            <div className={styles.disclaimer}>
                <span className={styles.disclaimerIcon}><MdOutlineInfo /></span>
                <span className={styles.disclaimerText}>
                    Responses are AI-generated. User discreation is advised.
                </span>
            </div>
        </div>
    );
};
